import { sanitizeHtml } from "../utils/sanitize";
import React, { useState, useEffect } from 'react';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import { Plus, Save, Trash2, Wallet, Eye, Code, FileText, Type, Image, Table, AlignLeft, DollarSign, Calendar, Repeat, Play, Pause, RefreshCw, History, RotateCcw, Clock, Edit, Tag, X } from 'lucide-react';
import { toast } from 'sonner';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { format } from 'date-fns';
import { formatInCST } from '../utils/timezone';

export default function ARSettings() {
  const [settings, setSettings] = useState(null);
  const [paymentTypes, setPaymentTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [editMode, setEditMode] = useState('code'); // 'code' or 'builder'
  const [templateBlocks, setTemplateBlocks] = useState([]);
  const [settingsForm, setSettingsForm] = useState({
    invoice_prefix: 'INV',
    invoice_next_number: 1,
    randomize_invoice_numbers: false,
    invoice_template: '',
    default_payment_terms: 'Net 30',
    default_tax_rate: 0.0,
    notes_template: ''
  });
  const [paymentTypeForm, setPaymentTypeForm] = useState({
    name: '',
    description: '',
    icon: ''
  });
  const [dialogOpen, setDialogOpen] = useState(false);
  const [recurringInvoices, setRecurringInvoices] = useState([]);
  const [recurringLoading, setRecurringLoading] = useState(false);
  
  // Version history state
  const [templateVersions, setTemplateVersions] = useState([]);
  const [versionsLoading, setVersionsLoading] = useState(false);
  const [versionHistoryOpen, setVersionHistoryOpen] = useState(false);
  const [selectedVersion, setSelectedVersion] = useState(null);
  const [versionPreviewOpen, setVersionPreviewOpen] = useState(false);
  const [editingVersionId, setEditingVersionId] = useState(null);
  const [editTagForm, setEditTagForm] = useState({ tag: '', notes: '' });

  const availableBlocks = [
    {
      id: 'header',
      type: 'header',
      label: 'Header',
      icon: Type,
      template: '<div style="text-align: center; padding: 20px; background: #f3f4f6;"><h1 style="margin: 0; color: #1f2937;">INVOICE</h1><p style="color: #6b7280;">Your Company Name</p></div>'
    },
    {
      id: 'company-info',
      type: 'info',
      label: 'Company Info',
      icon: FileText,
      template: '<div style="padding: 20px;"><p><strong>Company Name</strong></p><p>123 Business St</p><p>City, State 12345</p><p>Phone: (555) 123-4567</p></div>'
    },
    {
      id: 'invoice-details',
      type: 'details',
      label: 'Invoice Details',
      icon: Calendar,
      template: '<div style="padding: 20px;"><p><strong>Invoice #:</strong> {{invoice_number}}</p><p><strong>Date:</strong> {{issue_date}}</p><p><strong>Due Date:</strong> {{due_date}}</p><p><strong>Customer:</strong> {{customer_name}}</p></div>'
    },
    {
      id: 'line-items-table',
      type: 'table',
      label: 'Line Items Table',
      icon: Table,
      template: '<table style="width: 100%; border-collapse: collapse; margin: 20px 0;"><thead><tr style="background: #1f2937; color: white;"><th style="padding: 10px; text-align: left;">Description</th><th style="padding: 10px; text-align: center;">Qty</th><th style="padding: 10px; text-align: right;">Price</th><th style="padding: 10px; text-align: right;">Amount</th></tr></thead><tbody>{{line_items}}</tbody></table>'
    },
    {
      id: 'totals',
      type: 'totals',
      label: 'Totals Section',
      icon: DollarSign,
      template: '<div style="padding: 20px; text-align: right;"><p>Subtotal: ${{subtotal}}</p><p>Tax: ${{tax_amount}}</p><p style="font-size: 1.2em; font-weight: bold; border-top: 2px solid #000; padding-top: 10px;">Total: ${{total}}</p></div>'
    },
    {
      id: 'notes',
      type: 'notes',
      label: 'Notes/Terms',
      icon: AlignLeft,
      template: '<div style="padding: 20px; background: #fef3c7; border-left: 4px solid #f59e0b;"><p><strong>Payment Terms:</strong> Net 30</p><p>Thank you for your business!</p></div>'
    }
  ];

  const loadData = async () => {
    try {
      const [settingsRes, paymentTypesRes] = await Promise.all([
        api.get('/invoice-settings'),
        api.get('/payment-types')
      ]);
      setSettings(settingsRes.data);
      setSettingsForm({
        invoice_prefix: settingsRes.data.invoice_prefix || 'INV',
        invoice_next_number: settingsRes.data.invoice_next_number || 1,
        randomize_invoice_numbers: settingsRes.data.randomize_invoice_numbers || false,
        invoice_template: settingsRes.data.invoice_template || '',
        default_payment_terms: settingsRes.data.default_payment_terms || 'Net 30',
        default_tax_rate: settingsRes.data.default_tax_rate || 0.0,
        notes_template: settingsRes.data.notes_template || ''
      });
      setPaymentTypes(paymentTypesRes.data);
      
      // Initialize template blocks if template exists
      if (settingsRes.data.invoice_template) {
        // Parse existing template into blocks (simplified)
        setTemplateBlocks([]);
      }
    } catch (error) {
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
    loadRecurringInvoices();
  }, []);

  // Load template versions
  const loadTemplateVersions = async () => {
    setVersionsLoading(true);
    try {
      const response = await api.get('/invoice-template-versions');
      setTemplateVersions(response.data);
    } catch (error) {
      console.error('Failed to load template versions');
    } finally {
      setVersionsLoading(false);
    }
  };

  // Restore a template version
  const handleRestoreVersion = async (versionId) => {
    if (!window.confirm('Are you sure you want to restore this version? Your current template will be replaced.')) return;
    
    try {
      const response = await api.post(`/invoice-template-versions/${versionId}/restore`);
      setSettingsForm({ ...settingsForm, invoice_template: response.data.invoice_template || '' });
      toast.success('Template version restored successfully');
      setVersionHistoryOpen(false);
      loadTemplateVersions();
    } catch (error) {
      toast.error('Failed to restore version');
    }
  };

  // Delete a template version
  const handleDeleteVersion = async (versionId) => {
    if (!window.confirm('Are you sure you want to delete this version? This cannot be undone.')) return;
    
    try {
      await api.delete(`/invoice-template-versions/${versionId}`);
      toast.success('Version deleted');
      loadTemplateVersions();
    } catch (error) {
      toast.error('Failed to delete version');
    }
  };

  // Start editing a version's tag
  const startEditTag = (version) => {
    setEditingVersionId(version.id);
    setEditTagForm({ tag: version.tag || '', notes: version.notes || '' });
  };

  // Save version tag/notes
  const handleSaveTag = async () => {
    if (!editingVersionId) return;
    try {
      await api.patch(`/invoice-template-versions/${editingVersionId}`, editTagForm);
      toast.success('Version tag saved');
      setEditingVersionId(null);
      loadTemplateVersions();
    } catch (error) {
      toast.error('Failed to save tag');
    }
  };

  // Open version history dialog
  const openVersionHistory = () => {
    loadTemplateVersions();
    setVersionHistoryOpen(true);
  };

  const loadRecurringInvoices = async () => {
    setRecurringLoading(true);
    try {
      const response = await api.get('/recurring-invoices');
      setRecurringInvoices(response.data);
    } catch (error) {
      console.error('Failed to load recurring invoices');
    } finally {
      setRecurringLoading(false);
    }
  };

  const handleGenerateInvoice = async (invoiceId) => {
    try {
      const response = await api.post(`/recurring-invoices/${invoiceId}/generate`);
      toast.success(`Invoice ${response.data.invoice_number} generated`);
      loadRecurringInvoices();
    } catch (error) {
      toast.error('Failed to generate invoice');
    }
  };

  const handleToggleRecurring = async (invoiceId, enable) => {
    try {
      await api.put(`/recurring-invoices/${invoiceId}/toggle`, { is_recurring: enable });
      toast.success(enable ? 'Recurring enabled' : 'Recurring disabled');
      loadRecurringInvoices();
    } catch (error) {
      toast.error('Failed to update');
    }
  };

  const handleSettingsSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    
    try {
      // If in builder mode, compile blocks to HTML
      let finalTemplate = settingsForm.invoice_template;
      if (editMode === 'builder' && templateBlocks.length > 0) {
        finalTemplate = templateBlocks.map(block => block.template).join('\n');
      }
      
      await api.put('/invoice-settings', { ...settingsForm, invoice_template: finalTemplate });
      toast.success('Invoice settings saved successfully');
      loadData();
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handlePaymentTypeSubmit = async (e) => {
    e.preventDefault();
    
    try {
      await api.post('/payment-types', paymentTypeForm);
      toast.success('Payment type added successfully');
      setDialogOpen(false);
      setPaymentTypeForm({ name: '', description: '', icon: '' });
      loadData();
    } catch (error) {
      toast.error('Failed to add payment type');
    }
  };

  const handleDeletePaymentType = async (id) => {
    if (!window.confirm('Are you sure you want to delete this payment type?')) return;
    
    try {
      await api.delete(`/payment-types/${id}`);
      toast.success('Payment type deleted');
      loadData();
    } catch (error) {
      toast.error('Failed to delete payment type');
    }
  };

  const handleTogglePaymentType = async (id, currentStatus) => {
    try {
      await api.put(`/payment-types/${id}`, { is_active: !currentStatus });
      toast.success('Payment type updated');
      loadData();
    } catch (error) {
      toast.error('Failed to update payment type');
    }
  };

  const defaultPaymentTypes = [
    { name: 'Cash', icon: '💵' },
    { name: 'Check', icon: '✅' },
    { name: 'Credit Card', icon: '💳' },
    { name: 'Debit Card', icon: '💳' },
    { name: 'ACH/Bank Transfer', icon: '🏦' },
    { name: 'Zelle', icon: '⚡' },
    { name: 'Venmo', icon: '📱' },
    { name: 'CashApp', icon: '💰' },
    { name: 'PayPal', icon: '🅿️' },
    { name: 'Wire Transfer', icon: '🔄' },
  ];

  const addDefaultPaymentTypes = async () => {
    try {
      for (const pt of defaultPaymentTypes) {
        await api.post('/payment-types', { ...pt, description: '', is_active: true });
      }
      toast.success('Default payment types added');
      loadData();
    } catch (error) {
      toast.error('Some payment types may already exist');
      loadData();
    }
  };

  const insertVariable = (variable) => {
    const varText = `{{${variable}}}`;
    setSettingsForm({
      ...settingsForm,
      invoice_template: settingsForm.invoice_template + varText
    });
  };

  const insertHTMLElement = (element) => {
    const elements = {
      heading: '<h2 style="font-size:22px; font-weight:700; color:#111; margin:0 0 8px;">Heading Text</h2>',
      paragraph: '<p style="font-size:14px; color:#374151; line-height:1.6;">Your text here</p>',
      table: '<table style="width: 100%; border-collapse: collapse;"><tr><th style="border: 1px solid #ddd; padding: 8px;">Column 1</th><th style="border: 1px solid #ddd; padding: 8px;">Column 2</th></tr><tr><td style="border: 1px solid #ddd; padding: 8px;">Data 1</td><td style="border: 1px solid #ddd; padding: 8px;">Data 2</td></tr></table>',
      divider: '<hr style="margin: 20px 0; border: none; border-top: 1px solid #ddd;" />',
      spacer: '<div style="height: 20px;"></div>',
      two_column: `<table style="width:100%; border-collapse:collapse;">
  <tr>
    <td style="vertical-align:top; width:50%; padding-right:16px;">
      <p style="font-size:14px; color:#374151;">Left column content</p>
    </td>
    <td style="vertical-align:top; width:50%; padding-left:16px;">
      <p style="font-size:14px; color:#374151;">Right column content</p>
    </td>
  </tr>
</table>`,
      header_logo: `<div style="background:{{primary_color}}; color:white; padding:28px 32px; border-radius:8px 8px 0 0;">
  <table style="width:100%; border-collapse:collapse;">
    <tr>
      <td style="vertical-align:middle; width:60%;">
        {{company_logo}}
        <div style="font-size:24px; font-weight:700; margin-top:6px;">{{company_name}}</div>
      </td>
      <td style="vertical-align:middle; text-align:right;">
        <div style="font-size:28px; font-weight:700; opacity:0.9;">INVOICE</div>
        <div style="font-size:14px; margin-top:4px; opacity:0.85;">#{{invoice_number}}</div>
      </td>
    </tr>
  </table>
</div>`,
      bill_to_section: `<div style="padding:24px 32px; border-bottom:1px solid #e5e7eb;">
  <table style="width:100%; border-collapse:collapse;">
    <tr>
      <td style="vertical-align:top; width:55%;">
        <div style="font-size:11px; text-transform:uppercase; letter-spacing:1px; color:#9ca3af; margin-bottom:6px;">Bill To</div>
        <div style="font-size:16px; font-weight:600; color:#111;">{{customer_name}}</div>
      </td>
      <td style="vertical-align:top; text-align:right;">
        <table style="border-collapse:collapse; margin-left:auto;">
          <tr><td style="padding:3px 12px 3px 0; color:#6b7280; font-size:13px;">Issue Date:</td><td style="padding:3px 0; font-size:13px; font-weight:600;">{{issue_date}}</td></tr>
          <tr><td style="padding:3px 12px 3px 0; color:#6b7280; font-size:13px;">Due Date:</td><td style="padding:3px 0; font-size:13px; font-weight:600;">{{due_date}}</td></tr>
        </table>
      </td>
    </tr>
  </table>
</div>`,
      line_items_table: `<table style="width:100%; border-collapse:collapse; margin:24px 0; font-size:14px;">
  <thead>
    <tr style="background:#f3f4f6;">
      <th style="padding:12px 10px; text-align:left; border-bottom:2px solid #d1d5db; font-weight:600; color:#374151;">Description</th>
      <th style="padding:12px 10px; text-align:center; width:60px; border-bottom:2px solid #d1d5db; font-weight:600; color:#374151;">Qty</th>
      <th style="padding:12px 10px; text-align:right; width:110px; border-bottom:2px solid #d1d5db; font-weight:600; color:#374151;">Unit Price</th>
      <th style="padding:12px 10px; text-align:right; width:110px; border-bottom:2px solid #d1d5db; font-weight:600; color:#374151;">Amount</th>
    </tr>
  </thead>
  <tbody>{{line_items}}</tbody>
</table>`,
      totals_block: `<table style="width:260px; margin-left:auto; border-collapse:collapse; font-size:14px;">
  <tr><td style="padding:6px 0; color:#6b7280;">Subtotal:</td><td style="padding:6px 0; text-align:right;">\${{subtotal}}</td></tr>
  <tr><td style="padding:6px 0; color:#6b7280;">Tax:</td><td style="padding:6px 0; text-align:right;">\${{tax_amount}}</td></tr>
  <tr style="border-top:2px solid #111;"><td style="padding:10px 0; font-size:18px; font-weight:700;">Total Due:</td><td style="padding:10px 0; text-align:right; font-size:18px; font-weight:700;">\${{total}}</td></tr>
</table>`,
      footer: `<div style="background:#f9fafb; padding:16px 32px; border-top:1px solid #e5e7eb; text-align:center; border-radius:0 0 8px 8px;">
  <p style="margin:0; color:#9ca3af; font-size:11px;">{{company_name}} — Thank you for your business</p>
</div>`,
    };
    
    setSettingsForm({
      ...settingsForm,
      invoice_template: settingsForm.invoice_template + '\n' + (elements[element] || '')
    });
  };

  const addBlockToTemplate = (block) => {
    const newBlock = { ...block, id: `${block.id}-${Date.now()}` };
    setTemplateBlocks([...templateBlocks, newBlock]);
  };

  const onDragEnd = (result) => {
    if (!result.destination) return;
    
    const items = Array.from(templateBlocks);
    const [reordered] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reordered);
    
    setTemplateBlocks(items);
  };

  const removeBlock = (index) => {
    setTemplateBlocks(templateBlocks.filter((_, i) => i !== index));
  };

  const renderPreview = () => {
    let previewHTML = editMode === 'code' 
      ? settingsForm.invoice_template 
      : templateBlocks.map(block => block.template).join('\n');
    
    // Replace variables with sample data
    previewHTML = previewHTML
      .replace(/\{\{invoice_number\}\}/g, 'INV-000123')
      .replace(/\{\{customer_name\}\}/g, 'Acme Corporation')
      .replace(/\{\{issue_date\}\}/g, new Date().toLocaleDateString())
      .replace(/\{\{due_date\}\}/g, new Date(Date.now() + 30*24*60*60*1000).toLocaleDateString())
      .replace(/\{\{subtotal\}\}/g, '1,500.00')
      .replace(/\{\{tax_amount\}\}/g, '123.75')
      .replace(/\{\{total\}\}/g, '1,623.75')
      .replace(/\{\{company_name\}\}/g, 'CipherOne')
      .replace(/\{\{company_logo\}\}/g, '<img src="/cipherone-logo.png" alt="Logo" style="max-height:48px;width:auto;" />')
      .replace(/\{\{primary_color\}\}/g, '#1e40af')
      .replace(/\{\{notes\}\}/g, '<div style="padding:0 32px 20px;"><div style="padding:12px 16px;background:#f9fafb;border-radius:6px;font-size:13px;color:#6b7280;border:1px solid #e5e7eb;">Payment is due within 30 days. Thank you!</div></div>')
      .replace(/\{\{line_items\}\}/g, '<tr><td style="padding:10px; border-bottom:1px solid #e5e7eb;">Website Development &amp; Hosting</td><td style="padding:10px; text-align:center; border-bottom:1px solid #e5e7eb;">1</td><td style="padding:10px; text-align:right; border-bottom:1px solid #e5e7eb;">$1,200.00</td><td style="padding:10px; text-align:right; border-bottom:1px solid #e5e7eb;">$1,200.00</td></tr><tr><td style="padding:10px; border-bottom:1px solid #e5e7eb;">IT Support (2 hours)</td><td style="padding:10px; text-align:center; border-bottom:1px solid #e5e7eb;">2</td><td style="padding:10px; text-align:right; border-bottom:1px solid #e5e7eb;">$150.00</td><td style="padding:10px; text-align:right; border-bottom:1px solid #e5e7eb;">$300.00</td></tr>');
    
    return previewHTML;
  };

  if (loading) {
    return (
      <div className="co-page co-scope co-settings flex flex-col items-center justify-center min-h-[60vh]">
        <img src="/cipherone-logo.png" alt="Loading" className="h-16 w-16 logo-spin mb-4" />
        <div className="text-lg text-gray-600">Loading settings...</div>
      </div>
    );
  }

  return (
    <div className="co-page co-scope co-settings space-y-6" data-testid="ar-settings-page">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold">AR Settings</h1>
        <p className="text-gray-600 mt-1">Configure invoice settings, templates, and payment types</p>
      </div>

      {/* Info Banner */}
      <Card className="border-blue-200 bg-blue-50">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <DollarSign className="h-5 w-5 text-blue-600 mt-0.5" />
            <div className="flex-1">
              <h3 className="font-semibold text-blue-900 mb-1">Accounts Receivable Configuration</h3>
              <p className="text-sm text-blue-800">
                Configure how invoices are generated and numbered. Invoices are automatically numbered as <strong>MMDD-XXXXXX</strong> where:
              </p>
              <ul className="text-sm text-blue-800 mt-2 ml-4 list-disc space-y-1">
                <li><strong>MMDD</strong> = Current month and day (e.g., 0128 for January 28th)</li>
                <li><strong>XXXXXX</strong> = 6 random numeric characters for uniqueness</li>
              </ul>
              <p className="text-sm text-blue-800 mt-2">
                This format matches your ticket numbering system for consistency across the platform.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      <Tabs defaultValue="general" className="space-y-4">
        <TabsList>
          <TabsTrigger value="general">General Settings</TabsTrigger>
          <TabsTrigger value="template">Invoice Template</TabsTrigger>
          <TabsTrigger value="payments">Payment Types</TabsTrigger>
          <TabsTrigger value="recurring">Recurring Invoices</TabsTrigger>
        </TabsList>

        <form onSubmit={handleSettingsSubmit}>
          <TabsContent value="general" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>Invoice Number Configuration</CardTitle>
                <CardDescription>Configure how invoice numbers are generated</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="invoice_prefix">Invoice Prefix</Label>
                    <Input
                      id="invoice_prefix"
                      value={settingsForm.invoice_prefix}
                      onChange={(e) => setSettingsForm({...settingsForm, invoice_prefix: e.target.value})}
                      placeholder="INV"
                    />
                    <p className="text-xs text-gray-500 mt-1">Example: INV-000001</p>
                  </div>
                  <div>
                    <Label htmlFor="invoice_next_number">Next Invoice Number</Label>
                    <Input
                      id="invoice_next_number"
                      type="number"
                      value={settingsForm.invoice_next_number}
                      onChange={(e) => setSettingsForm({...settingsForm, invoice_next_number: parseInt(e.target.value)})}
                      min="1"
                      disabled={settingsForm.randomize_invoice_numbers}
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      {settingsForm.randomize_invoice_numbers ? 'Random mode enabled' : `Next: ${settingsForm.invoice_prefix}-${String(settingsForm.invoice_next_number).padStart(6, '0')}`}
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <input
                    type="checkbox"
                    id="randomize_invoice_numbers"
                    checked={settingsForm.randomize_invoice_numbers}
                    onChange={(e) => setSettingsForm({...settingsForm, randomize_invoice_numbers: e.target.checked})}
                    className="w-4 h-4 mt-1"
                  />
                  <div className="flex-1">
                    <Label htmlFor="randomize_invoice_numbers" className="cursor-pointer font-semibold text-blue-900">
                      Use Randomized Invoice Numbers
                    </Label>
                    <p className="text-xs text-blue-800 mt-1">
                      Generate random invoice numbers (e.g., INV-A7F2K9) instead of sequential numbers for enhanced privacy and security. Prevents customers from estimating your business volume.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Default Settings</CardTitle>
                <CardDescription>Default values for new invoices</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="default_payment_terms">Payment Terms</Label>
                    <Select value={settingsForm.default_payment_terms} onValueChange={(value) => setSettingsForm({...settingsForm, default_payment_terms: value})}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Due on Receipt">Due on Receipt</SelectItem>
                        <SelectItem value="Net 15">Net 15</SelectItem>
                        <SelectItem value="Net 30">Net 30</SelectItem>
                        <SelectItem value="Net 60">Net 60</SelectItem>
                        <SelectItem value="Net 90">Net 90</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="default_tax_rate">Default Tax Rate (%)</Label>
                    <Input
                      id="default_tax_rate"
                      type="number"
                      step="0.01"
                      value={settingsForm.default_tax_rate}
                      onChange={(e) => setSettingsForm({...settingsForm, default_tax_rate: parseFloat(e.target.value)})}
                    />
                  </div>
                </div>
                <div>
                  <Label htmlFor="notes_template">Default Invoice Notes</Label>
                  <Textarea
                    id="notes_template"
                    value={settingsForm.notes_template}
                    onChange={(e) => setSettingsForm({...settingsForm, notes_template: e.target.value})}
                    rows={3}
                    placeholder="Thank you for your business!"
                  />
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="template" className="space-y-4">
            <Card>
              <CardHeader>
                <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <Code className="h-5 w-5" />
                      Invoice Template Editor
                    </CardTitle>
                    <CardDescription>Design your invoice template with drag-and-drop or HTML</CardDescription>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <Button type="button" variant="outline" onClick={openVersionHistory} data-testid="version-history-btn">
                      <History className="h-4 w-4 mr-2" />
                      Version History
                    </Button>
                    <Button type="button" variant="outline" onClick={() => setEditMode(editMode === 'code' ? 'builder' : 'code')}>
                      {editMode === 'code' ? 'Switch to Builder' : 'Switch to Code'}
                    </Button>
                    <Button type="button" variant="outline" onClick={() => setPreviewOpen(true)}>
                      <Eye className="h-4 w-4 mr-2" />
                      Preview
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                {editMode === 'code' ? (
                  <>
                    {/* Kitchen Sink - Insert Tools */}
                    <div className="border rounded-lg p-4 bg-gray-50">
                      <p className="text-sm font-semibold mb-3">Quick Insert Tools:</p>
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <p className="text-xs text-gray-600 mb-2">Variables:</p>
                          <div className="flex flex-wrap gap-1">
                            {['invoice_number', 'customer_name', 'issue_date', 'due_date', 'total', 'subtotal', 'tax_amount', 'line_items', 'company_name', 'company_logo', 'primary_color', 'notes'].map(v => (
                              <Button
                                key={v}
                                type="button"
                                size="sm"
                                variant="outline"
                                onClick={() => insertVariable(v)}
                                className="text-xs"
                              >
                                {v}
                              </Button>
                            ))}
                          </div>
                        </div>
                        <div>
                          <p className="text-xs text-gray-600 mb-2">Layout Blocks:</p>
                          <div className="flex flex-wrap gap-1">
                            <Button type="button" size="sm" variant="outline" onClick={() => insertHTMLElement('header_logo')} className="text-xs border-blue-200 text-blue-700">Header + Logo</Button>
                            <Button type="button" size="sm" variant="outline" onClick={() => insertHTMLElement('bill_to_section')} className="text-xs border-blue-200 text-blue-700">Bill To Section</Button>
                            <Button type="button" size="sm" variant="outline" onClick={() => insertHTMLElement('line_items_table')} className="text-xs border-blue-200 text-blue-700">Line Items Table</Button>
                            <Button type="button" size="sm" variant="outline" onClick={() => insertHTMLElement('totals_block')} className="text-xs border-blue-200 text-blue-700">Totals Block</Button>
                            <Button type="button" size="sm" variant="outline" onClick={() => insertHTMLElement('two_column')} className="text-xs border-blue-200 text-blue-700">2-Column Layout</Button>
                            <Button type="button" size="sm" variant="outline" onClick={() => insertHTMLElement('footer')} className="text-xs border-blue-200 text-blue-700">Footer</Button>
                          </div>
                          <p className="text-xs text-gray-600 mt-2 mb-2">Basic Elements:</p>
                          <div className="flex flex-wrap gap-1">
                            <Button type="button" size="sm" variant="outline" onClick={() => insertHTMLElement('heading')}>Heading</Button>
                            <Button type="button" size="sm" variant="outline" onClick={() => insertHTMLElement('paragraph')}>Paragraph</Button>
                            <Button type="button" size="sm" variant="outline" onClick={() => insertHTMLElement('table')}>Table</Button>
                            <Button type="button" size="sm" variant="outline" onClick={() => insertHTMLElement('divider')}>Divider</Button>
                            <Button type="button" size="sm" variant="outline" onClick={() => insertHTMLElement('spacer')}>Spacer</Button>
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    <div>
                      <Label>Template HTML Code</Label>
                      <Textarea
                        value={settingsForm.invoice_template}
                        onChange={(e) => setSettingsForm({...settingsForm, invoice_template: e.target.value})}
                        rows={20}
                        className="font-mono text-sm"
                        placeholder="Enter your custom invoice HTML template here..."
                      />
                    </div>
                  </>
                ) : (
                  <>
                    {/* Drag and Drop Builder */}
                    <div className="grid grid-cols-4 gap-4">
                      <div className="col-span-1 space-y-2">
                        <p className="text-sm font-semibold">Available Blocks:</p>
                        <p className="text-xs text-gray-600 mb-3">Click to add to template</p>
                        {availableBlocks.map((block) => {
                          const Icon = block.icon;
                          return (
                            <Button
                              key={block.id}
                              type="button"
                              variant="outline"
                              className="w-full justify-start"
                              onClick={() => addBlockToTemplate(block)}
                            >
                              <Icon className="h-4 w-4 mr-2" />
                              {block.label}
                            </Button>
                          );
                        })}
                      </div>
                      
                      <div className="col-span-3">
                        <p className="text-sm font-semibold mb-2">Template Preview (Drag to reorder):</p>
                        {templateBlocks.length === 0 ? (
                          <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center text-gray-500">
                            <FileText className="h-12 w-12 mx-auto text-gray-400 mb-2" />
                            <p>Click blocks on the left to build your template</p>
                          </div>
                        ) : (
                          <DragDropContext onDragEnd={onDragEnd}>
                            <Droppable droppableId="template-blocks">
                              {(provided) => (
                                <div {...provided.droppableProps} ref={provided.innerRef} className="space-y-2">
                                  {templateBlocks.map((block, index) => {
                                    const Icon = block.icon;
                                    return (
                                      <Draggable key={block.id} draggableId={block.id} index={index}>
                                        {(provided) => (
                                          <div
                                            ref={provided.innerRef}
                                            {...provided.draggableProps}
                                            {...provided.dragHandleProps}
                                            className="border rounded-lg p-3 bg-[#0b2130] hover:shadow-md transition-shadow"
                                          >
                                            <div className="flex items-center justify-between">
                                              <div className="flex items-center gap-2">
                                                <Icon className="h-4 w-4 text-gray-600" />
                                                <span className="font-medium">{block.label}</span>
                                              </div>
                                              <Button
                                                type="button"
                                                size="sm"
                                                variant="ghost"
                                                onClick={() => removeBlock(index)}
                                              >
                                                <Trash2 className="h-4 w-4 text-red-600" />
                                              </Button>
                                            </div>
                                          </div>
                                        )}
                                      </Draggable>
                                    );
                                  })}
                                  {provided.placeholder}
                                </div>
                              )}
                            </Droppable>
                          </DragDropContext>
                        )}
                      </div>
                    </div>
                  </>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="payments" className="space-y-4">
            <Card>
              <CardHeader>
                <div className="flex justify-between items-center">
                  <div>
                    <CardTitle>Payment Types</CardTitle>
                    <CardDescription>Manage accepted payment methods</CardDescription>
                  </div>
                  <div className="flex gap-2">
                    <Button type="button" variant="outline" onClick={addDefaultPaymentTypes}>
                      <Plus className="h-4 w-4 mr-2" />
                      Add Default Types
                    </Button>
                    <Button type="button" onClick={() => setDialogOpen(true)}>
                      <Plus className="h-4 w-4 mr-2" />
                      Add Custom Type
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {paymentTypes.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <Wallet className="h-12 w-12 mx-auto text-gray-400 mb-2" />
                    <p>No payment types configured</p>
                    <Button className="mt-4" onClick={addDefaultPaymentTypes}>
                      <Plus className="h-4 w-4 mr-2" />
                      Add Default Payment Types
                    </Button>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {paymentTypes.map((pt) => (
                      <Card key={pt.id} className="hover:shadow-md transition-shadow">
                        <CardContent className="p-4">
                          <div className="flex items-start justify-between">
                            <div className="flex items-center gap-3">
                              <div className="text-2xl">{pt.icon || '💳'}</div>
                              <div>
                                <h3 className="font-semibold">{pt.name}</h3>
                                {pt.description && (
                                  <p className="text-xs text-gray-600">{pt.description}</p>
                                )}
                              </div>
                            </div>
                            <div className="flex gap-1">
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => handleTogglePaymentType(pt.id, pt.is_active)}
                              >
                                <Badge variant={pt.is_active ? 'default' : 'secondary'}>
                                  {pt.is_active ? 'Active' : 'Inactive'}
                                </Badge>
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => handleDeletePaymentType(pt.id)}
                              >
                                <Trash2 className="h-4 w-4 text-red-600" />
                              </Button>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Recurring Invoices Tab */}
          <TabsContent value="recurring" className="space-y-4">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <Repeat className="h-5 w-5 text-blue-600" />
                      Recurring Invoice Schedules
                    </CardTitle>
                    <CardDescription>
                      Manage invoices that automatically generate on a schedule. Mark any invoice as recurring from the Invoices page.
                    </CardDescription>
                  </div>
                  <Button variant="outline" size="sm" onClick={loadRecurringInvoices} disabled={recurringLoading}>
                    <RefreshCw className={`h-4 w-4 mr-1 ${recurringLoading ? 'animate-spin' : ''}`} />
                    Refresh
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {recurringLoading ? (
                  <div className="text-center py-8 text-neutral-500">Loading recurring invoices...</div>
                ) : recurringInvoices.length === 0 ? (
                  <div className="text-center py-8 text-neutral-500">
                    <Repeat className="h-10 w-10 mx-auto text-neutral-300 mb-3" />
                    <p className="font-medium">No recurring invoices</p>
                    <p className="text-sm mt-1">Go to the Invoices page and click "Make Recurring" on any invoice to set up a schedule.</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {recurringInvoices.map((inv) => (
                      <div key={inv.id} className="flex items-center justify-between p-4 border rounded-lg hover:bg-neutral-50" data-testid={`recurring-invoice-${inv.id}`}>
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="font-mono text-sm font-medium">{inv.invoice_number || 'No number'}</span>
                            <Badge className="bg-blue-100 text-blue-800 capitalize">{inv.recurring_frequency || 'monthly'}</Badge>
                            {inv.is_recurring ? (
                              <Badge className="bg-emerald-100 text-emerald-800">Active</Badge>
                            ) : (
                              <Badge className="bg-neutral-100 text-neutral-600">Paused</Badge>
                            )}
                          </div>
                          <p className="text-sm text-neutral-700">{inv.customer_name || 'No customer'}</p>
                          <div className="flex items-center gap-4 text-xs text-neutral-500 mt-1">
                            <span>Total: <strong className="text-neutral-700">${(inv.total || 0).toFixed(2)}</strong></span>
                            <span>{(inv.line_items || []).length} line item(s)</span>
                            {inv.recurring_start_date && (
                              <span>Start: {(() => { try { return formatInCST(inv.recurring_start_date, 'MMM d, yyyy'); } catch { return '-'; } })()}</span>
                            )}
                            {inv.recurring_end_date && (
                              <span>End: {(() => { try { return formatInCST(inv.recurring_end_date, 'MMM d, yyyy'); } catch { return '-'; } })()}</span>
                            )}
                            {inv.last_generated && (
                              <span>Last generated: {(() => { try { return formatInCST(inv.last_generated, 'MMM d, yyyy'); } catch { return '-'; } })()}</span>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-2 ml-4">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleGenerateInvoice(inv.id)}
                            data-testid={`generate-invoice-${inv.id}`}
                          >
                            <Play className="h-3.5 w-3.5 mr-1" />
                            Generate Now
                          </Button>
                          {inv.is_recurring ? (
                            <Button
                              size="sm"
                              variant="outline"
                              className="text-amber-600 hover:text-amber-700"
                              onClick={() => handleToggleRecurring(inv.id, false)}
                            >
                              <Pause className="h-3.5 w-3.5 mr-1" />
                              Pause
                            </Button>
                          ) : (
                            <Button
                              size="sm"
                              variant="outline"
                              className="text-emerald-600 hover:text-emerald-700"
                              onClick={() => handleToggleRecurring(inv.id, true)}
                            >
                              <Play className="h-3.5 w-3.5 mr-1" />
                              Resume
                            </Button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <div className="flex justify-end">
            <Button type="submit" disabled={saving}>
              <Save className="h-4 w-4 mr-2" />
              {saving ? 'Saving...' : 'Save All Settings'}
            </Button>
          </div>
        </form>
      </Tabs>

      {/* Preview Dialog */}
      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="co-scope co-settings max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Invoice Template Preview</DialogTitle>
          </DialogHeader>
          <div className="border rounded-lg p-6 bg-white">
            <div dangerouslySetInnerHTML={{ __html: sanitizeHtml(renderPreview()) }} />
          </div>
          <div className="flex justify-end">
            <Button onClick={() => setPreviewOpen(false)}>Close Preview</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Version History Dialog */}
      <Dialog open={versionHistoryOpen} onOpenChange={setVersionHistoryOpen}>
        <DialogContent className="co-scope co-settings max-w-2xl max-h-[85vh] overflow-hidden flex flex-col" data-testid="version-history-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <History className="h-5 w-5 text-blue-600" />
              Template Version History
            </DialogTitle>
            <DialogDescription>
              View and restore previous versions of your invoice template. All versions are kept forever unless deleted.
            </DialogDescription>
          </DialogHeader>
          
          <ScrollArea className="flex-1 pr-4">
            {versionsLoading ? (
              <div className="text-center py-8 text-gray-500">Loading versions...</div>
            ) : templateVersions.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <History className="h-10 w-10 mx-auto text-gray-300 mb-3" />
                <p className="font-medium">No version history yet</p>
                <p className="text-sm mt-1">Versions will be saved automatically when you update the template.</p>
              </div>
            ) : (
              <div className="space-y-3">
                {templateVersions.map((version, index) => (
                  <div 
                    key={version.id} 
                    className={`p-4 border rounded-lg transition-colors ${editingVersionId === version.id ? 'bg-blue-50 border-blue-200' : 'hover:bg-gray-50'}`}
                    data-testid={`version-item-${version.id}`}
                  >
                    {editingVersionId === version.id ? (
                      /* Edit Mode */
                      <div className="space-y-3">
                        <div className="flex items-center gap-2 mb-2">
                          <Badge variant="outline" className="bg-blue-50 text-blue-700">v{version.version_number}</Badge>
                          <span className="text-sm text-gray-500">Editing tag & notes</span>
                        </div>
                        <div>
                          <Label className="text-xs text-gray-600">Tag Name</Label>
                          <Input 
                            placeholder="e.g., Q1 2026, Holiday Template, Standard..." 
                            value={editTagForm.tag}
                            onChange={(e) => setEditTagForm({ ...editTagForm, tag: e.target.value })}
                            className="h-8 text-sm"
                            data-testid="edit-tag-input"
                          />
                        </div>
                        <div>
                          <Label className="text-xs text-gray-600">Notes</Label>
                          <Input 
                            placeholder="Description of this version..."
                            value={editTagForm.notes}
                            onChange={(e) => setEditTagForm({ ...editTagForm, notes: e.target.value })}
                            className="h-8 text-sm"
                            data-testid="edit-notes-input"
                          />
                        </div>
                        <div className="flex justify-end gap-2">
                          <Button size="sm" variant="outline" onClick={() => setEditingVersionId(null)}>
                            <X className="h-3.5 w-3.5 mr-1" /> Cancel
                          </Button>
                          <Button size="sm" onClick={handleSaveTag} className="bg-blue-600 hover:bg-blue-700">
                            <Save className="h-3.5 w-3.5 mr-1" /> Save
                          </Button>
                        </div>
                      </div>
                    ) : (
                      /* View Mode */
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1 flex-wrap">
                            <Badge variant="outline" className="bg-blue-50 text-blue-700">
                              v{version.version_number}
                            </Badge>
                            {index === 0 && (
                              <Badge className="bg-green-100 text-green-800 text-xs">Latest</Badge>
                            )}
                            {version.tag && (
                              <Badge className="bg-purple-100 text-purple-800 text-xs">
                                <Tag className="h-2.5 w-2.5 mr-1" />
                                {version.tag}
                              </Badge>
                            )}
                          </div>
                          <div className="flex items-center gap-2 text-sm text-gray-600">
                            <Clock className="h-3.5 w-3.5" />
                            <span>
                              {(() => {
                                try {
                                  return formatInCST(version.created_at, 'MMM d, yyyy h:mm a');
                                } catch {
                                  return version.created_at;
                                }
                              })()}
                            </span>
                          </div>
                          <p className="text-xs text-gray-500 mt-1">
                            by {version.created_by}
                          </p>
                          {version.notes && (
                            <p className="text-sm text-gray-600 mt-2 italic">"{version.notes}"</p>
                          )}
                        </div>
                        <div className="flex items-center gap-1 shrink-0">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => startEditTag(version)}
                            title="Edit tag & notes"
                            data-testid={`edit-version-${version.id}`}
                          >
                            <Edit className="h-3.5 w-3.5 text-gray-500" />
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => {
                              setSelectedVersion(version);
                              setVersionPreviewOpen(true);
                            }}
                            data-testid={`preview-version-${version.id}`}
                          >
                            <Eye className="h-3.5 w-3.5 mr-1" />
                            Preview
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-blue-600 hover:text-blue-700"
                            onClick={() => handleRestoreVersion(version.id)}
                            data-testid={`restore-version-${version.id}`}
                          >
                            <RotateCcw className="h-3.5 w-3.5 mr-1" />
                            Restore
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="text-red-500 hover:text-red-700 hover:bg-red-50"
                            onClick={() => handleDeleteVersion(version.id)}
                            data-testid={`delete-version-${version.id}`}
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </ScrollArea>
          
          <div className="flex justify-end pt-4 border-t mt-4">
            <Button variant="outline" onClick={() => setVersionHistoryOpen(false)}>Close</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Version Preview Dialog */}
      <Dialog open={versionPreviewOpen} onOpenChange={setVersionPreviewOpen}>
        <DialogContent className="co-scope co-settings max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              Preview: Version {selectedVersion?.version_number}
              <Badge variant="outline" className="ml-2">
                {selectedVersion && (() => {
                  try {
                    return formatInCST(selectedVersion.created_at, 'MMM d, yyyy');
                  } catch {
                    return '';
                  }
                })()}
              </Badge>
            </DialogTitle>
          </DialogHeader>
          <div className="border rounded-lg p-6 bg-white">
            <div dangerouslySetInnerHTML={{ __html: sanitizeHtml(
              (selectedVersion?.template_content || '')
                .replace(/\{\{invoice_number\}\}/g, 'INV-000123')
                .replace(/\{\{customer_name\}\}/g, 'Acme Corporation')
                .replace(/\{\{issue_date\}\}/g, new Date().toLocaleDateString())
                .replace(/\{\{due_date\}\}/g, new Date(Date.now() + 30*24*60*60*1000).toLocaleDateString())
                .replace(/\{\{subtotal\}\}/g, '1,500.00')
                .replace(/\{\{tax_amount\}\}/g, '120.00')
                .replace(/\{\{total\}\}/g, '1,620.00')
                .replace(/\{\{line_items\}\}/g, '<tr><td style="border: 1px solid #ddd; padding: 8px;">Sample Item</td><td style="border: 1px solid #ddd; padding: 8px; text-align: center;">1</td><td style="border: 1px solid #ddd; padding: 8px; text-align: right;">$1,500.00</td><td style="border: 1px solid #ddd; padding: 8px; text-align: right;">$1,500.00</td></tr>')
            ) }} />
          </div>
          <div className="flex justify-between">
            <Button 
              variant="outline" 
              className="text-blue-600"
              onClick={() => {
                handleRestoreVersion(selectedVersion?.id);
                setVersionPreviewOpen(false);
              }}
            >
              <RotateCcw className="h-4 w-4 mr-2" />
              Restore This Version
            </Button>
            <Button onClick={() => setVersionPreviewOpen(false)}>Close</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add Payment Type Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="co-scope co-settings">
          <DialogHeader>
            <DialogTitle>Add Payment Type</DialogTitle>
          </DialogHeader>
          <form onSubmit={handlePaymentTypeSubmit} className="space-y-4">
            <div>
              <Label htmlFor="pt_name">Payment Type Name *</Label>
              <Input
                id="pt_name"
                value={paymentTypeForm.name}
                onChange={(e) => setPaymentTypeForm({...paymentTypeForm, name: e.target.value})}
                required
                placeholder="e.g., Bitcoin"
              />
            </div>
            <div>
              <Label htmlFor="pt_icon">Icon (emoji or symbol)</Label>
              <Input
                id="pt_icon"
                value={paymentTypeForm.icon}
                onChange={(e) => setPaymentTypeForm({...paymentTypeForm, icon: e.target.value})}
                placeholder="₿"
                maxLength="2"
              />
            </div>
            <div>
              <Label htmlFor="pt_description">Description</Label>
              <Input
                id="pt_description"
                value={paymentTypeForm.description}
                onChange={(e) => setPaymentTypeForm({...paymentTypeForm, description: e.target.value})}
                placeholder="Optional description"
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
              <Button type="submit">Add Payment Type</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
